import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Chip,
  
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { organisationsApi, organisationContactsApi } from '../services/api';

const OrganisationContactSelector = ({ 
  selectedOrganisation, 
  selectedContact, 
  onOrganisationChange, 
  onContactChange,
  disabled = false,
  // When true, contacts cannot be selected or created until an organisation is selected
  disableContactUntilOrganisation = true,
}) => {
  const [organisations, setOrganisations] = useState([]);
  const [searchedOrganisations, setSearchedOrganisations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orgAutocompleteOpen, setOrgAutocompleteOpen] = useState(false);
  
  const [orgInputValue, setOrgInputValue] = useState('');
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const orgRequestSequenceRef = useRef(0);

  // Dialog states
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  
  // Form states
  const [newOrgForm, setNewOrgForm] = useState({
    name: '',
    address: '',
    email: '',
    phone: ''
  });
  
  const [newContactForm, setNewContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: ''
  });

  useEffect(() => {
    fetchOrganisations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOrganisation) {
      fetchContacts(selectedOrganisation.organisationid);
      // Keep the input text in sync with the selected organisation
      setOrgInputValue(selectedOrganisation.name || '');
      setOrgSearchTerm('');
    } else {
      setContacts([]);
      if (onContactChange) {
        onContactChange(null);
      }
      setOrgInputValue('');
      setOrgSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation]);

  const normalizeOrg = (org) => {
    if (!org) return null;
    const rawId = org.organisationid ?? org.organisation_id ?? org.id ?? org.OrganisationID ?? null;
    const organisationid = rawId != null ? String(rawId) : null;
    return {
      ...org,
      organisationid,
      organisation_id: organisationid,
      name: org.name ?? org.Name ?? '',
      email: org.email ?? org.Email ?? '',
      phone: org.phone ?? org.Phone ?? '',
    };
  };

  const matchesOrganisationSearch = (option, searchTerm) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return true;
    }

    const name = option?.name?.toLowerCase() || '';
    const email = option?.email?.toLowerCase() || '';
    const phone = option?.phone?.toLowerCase() || '';

    return (
      name.includes(normalizedSearch) ||
      email.includes(normalizedSearch) ||
      phone.includes(normalizedSearch)
    );
  };

  const mergeOrganisationOptions = (primaryOptions, secondaryOptions) => {
    const merged = [];
    const seenIds = new Set();

    [...primaryOptions, ...secondaryOptions].forEach((option) => {
      if (!option) {
        return;
      }

      const optionId = option.organisationid ?? option.organisation_id ?? option.id;
      const dedupeKey = optionId != null
        ? `id:${String(optionId)}`
        : `name:${(option.name || '').trim().toLowerCase()}`;

      if (seenIds.has(dedupeKey)) {
        return;
      }

      seenIds.add(dedupeKey);
      merged.push(option);
    });

    return merged;
  };

  const fetchOrganisations = async (searchTerm = '') => {
    const requestId = orgRequestSequenceRef.current + 1;
    orgRequestSequenceRef.current = requestId;

    try {
      setLoading(true);
      const response = searchTerm
        ? await organisationsApi.search(searchTerm)
        : await organisationsApi.getAll();

      if (requestId !== orgRequestSequenceRef.current) {
        return;
      }

      const data = response?.data;
      const allOrgsRaw = Array.isArray(data) ? data : [];
      const allOrgs = allOrgsRaw.map(normalizeOrg);
      if (searchTerm) {
        setSearchedOrganisations(allOrgs);
      } else {
        setOrganisations(allOrgs);
        setSearchedOrganisations([]);
      }
    } catch (err) {
      if (requestId !== orgRequestSequenceRef.current) {
        return;
      }

      console.error('Error fetching organisations:', err);
      if (searchTerm) {
        setSearchedOrganisations([]);
      } else {
        setOrganisations([]);
        setSearchedOrganisations([]);
      }
    } finally {
      if (requestId === orgRequestSequenceRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchContacts = async (organisationId) => {
    try {
      const response = await organisationsApi.getContacts(organisationId);
      
      // The API returns { contacts: [...], total: number }
      const contactsArray = response.data?.contacts || [];
      setContacts(contactsArray);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setContacts([]); // Ensure it's always an array
    }
  };

  const handleOrganisationSearch = (event, value, reason) => {
    const nextValue = value || '';

    if (reason === 'input') {
      setOrgInputValue(nextValue);
      setOrgAutocompleteOpen(true);
      const searchTerm = nextValue.trim();
      setOrgSearchTerm(searchTerm);
      fetchOrganisations(searchTerm);
      return;
    }

    if (reason === 'clear') {
      setOrgInputValue('');
      setOrgSearchTerm('');
      setOrgAutocompleteOpen(true);
      fetchOrganisations();
      return;
    }

    if (reason === 'reset') {
      setOrgSearchTerm('');
      if (selectedOrganisationOption) {
        setOrgInputValue(selectedOrganisationOption.name || '');
      }
    }
  };

  const selectedOrganisationOption = normalizeOrg(selectedOrganisation);
  const baseOrganisationOptions = Array.isArray(organisations) ? organisations : [];
  const searchedOrganisationOptions = Array.isArray(searchedOrganisations) ? searchedOrganisations : [];
  const locallyFilteredOrganisationOptions = orgSearchTerm
    ? baseOrganisationOptions.filter((option) => matchesOrganisationSearch(option, orgSearchTerm))
    : baseOrganisationOptions;
  const organisationOptions = orgSearchTerm
    ? mergeOrganisationOptions(locallyFilteredOrganisationOptions, searchedOrganisationOptions)
    : baseOrganisationOptions;
  const autocompleteOptions = selectedOrganisationOption && !organisationOptions.some((option) => {
    const optionId = option.organisationid ?? option.organisation_id ?? option.id;
    const selectedId = selectedOrganisationOption.organisationid ?? selectedOrganisationOption.organisation_id ?? selectedOrganisationOption.id;
    return String(optionId) === String(selectedId);
  })
    ? [selectedOrganisationOption, ...organisationOptions]
    : organisationOptions;
  const normalizedSearch = orgSearchTerm.toLowerCase();
  const filteredAutocompleteOptions = normalizedSearch
    ? autocompleteOptions.filter((option) => matchesOrganisationSearch(option, normalizedSearch))
    : autocompleteOptions;

  const handleCreateOrganisation = async () => {
    try {
      const response = await organisationsApi.create(newOrgForm);
      const raw = response.data || {};
      // Fallback to the entered name if API did not echo it back
      if (!raw.name && newOrgForm.name) {
        raw.name = newOrgForm.name;
      }
      const newOrganisation = normalizeOrg(raw);
      
      // Update lists
      setOrganisations(prev => [newOrganisation, ...prev]);
      
      // Immediately notify parent so UI reflects selection right away
      onOrganisationChange && onOrganisationChange(newOrganisation);
      // Ensure input shows the selected org name
      setOrgInputValue(newOrganisation.name || '');

      // No pending selection needed; we've updated lists and notified parent
      
      // Reset form and close dialog
      setNewOrgForm({ name: '', address: '', email: '', phone: '' });
      setOrgDialogOpen(false);
      
    } catch (err) {
      console.error('Error creating organisation:', err);
    }
  };

  const handleCreateContact = async () => {
    if (!selectedOrganisation) {
      return;
    }

    try {
      const contactData = {
        ...newContactForm,
        organisation_id: selectedOrganisation.organisationid
      };
      
      const response = await organisationContactsApi.create(contactData);
      const newContact = response.data;
      
      // Add to list and select it
      setContacts(prev => [newContact, ...prev]);
      onContactChange(newContact);
      
      // Reset form and close dialog
      setNewContactForm({ first_name: '', last_name: '', email: '', mobile: '' });
      setContactDialogOpen(false);
      
    } catch (err) {
      console.error('Error creating contact:', err);
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Organisation Selection */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">Organisation</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              fullWidth
              open={orgAutocompleteOpen}
              options={filteredAutocompleteOptions}
              ListboxProps={{ key: orgSearchTerm || 'all-organisations' }}
              filterOptions={(options) => options}
              getOptionLabel={(option) => option?.name || ''}
              openOnFocus
              clearOnBlur={false}
              // Ensure Autocomplete knows how to compare options to value by id
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                const oid = option.organisationid ?? option.organisation_id ?? option.id;
                const vid = value.organisationid ?? value.organisation_id ?? value.id;
                return String(oid) === String(vid);
              }}
              value={selectedOrganisationOption}
              inputValue={orgInputValue}
              onOpen={() => setOrgAutocompleteOpen(true)}
              onClose={() => setOrgAutocompleteOpen(false)}
              onChange={(event, newValue) => {
                onOrganisationChange(newValue);
                setOrgInputValue(newValue?.name || '');
                setOrgSearchTerm('');
                setOrgAutocompleteOpen(false);
              }}
              onInputChange={handleOrganisationSearch}
              disabled={disabled}
              loading={loading}
              noOptionsText={orgInputValue.trim() ? 'No matching organisations' : 'No organisations found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Organisation"
                  placeholder="Search for an organisation..."
                  helperText="Type to search for existing organisations"
                />
              )}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;

                return (
                  <Box component="li" key={key} {...optionProps}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      {option.email && (
                        <Typography variant="body2" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setOrgDialogOpen(true)}
              disabled={disabled}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              New
            </Button>
          </Box>
          
          {selectedOrganisation && (
            <Box sx={{ mt: 1 }}>
              <Chip
                icon={<BusinessIcon />}
                label={selectedOrganisation.name}
                color="primary"
                variant="outlined"
              />
              {selectedOrganisation.email && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedOrganisation.email}
                </Typography>
              )}
            </Box>
          )}
        </Grid>

        {/* Contact Selection */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6">Contact</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              fullWidth
              options={Array.isArray(contacts) ? contacts : []}
              getOptionLabel={(option) => option?.full_name || ''}
              value={selectedContact}
              onChange={(event, newValue) => onContactChange(newValue)}
              disabled={disabled || (disableContactUntilOrganisation && !selectedOrganisation)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Contact"
                  placeholder={selectedOrganisation ? "Select a contact..." : "Select an organisation first"}
                  helperText={
                    selectedOrganisation 
                      ? "Choose a contact from the selected organisation"
                      : "You must select an organisation before choosing a contact"
                  }
                />
              )}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;

                return (
                  <Box component="li" key={key} {...optionProps}>
                    <Box>
                      <Typography variant="body1">{option.full_name}</Typography>
                      {option.email && (
                        <Typography variant="body2" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                      {option.mobile && (
                        <Typography variant="body2" color="text.secondary">
                          {option.mobile}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setContactDialogOpen(true)}
              disabled={disabled || (disableContactUntilOrganisation && !selectedOrganisation)}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              New
            </Button>
          </Box>
          
          {selectedContact && (
            <Box sx={{ mt: 1 }}>
              <Chip
                icon={<PersonIcon />}
                label={selectedContact.full_name}
                color="secondary"
                variant="outlined"
              />
              {selectedContact.email && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedContact.email}
                </Typography>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Create Organisation Dialog */}
      <Dialog open={orgDialogOpen} onClose={() => setOrgDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organisation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Organisation Name"
              value={newOrgForm.name}
              onChange={(e) => setNewOrgForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newOrgForm.email}
              onChange={(e) => setNewOrgForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Phone"
              value={newOrgForm.phone}
              onChange={(e) => setNewOrgForm(prev => ({ ...prev, phone: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={3}
              value={newOrgForm.address}
              onChange={(e) => setNewOrgForm(prev => ({ ...prev, address: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateOrganisation}
            variant="contained"
            disabled={!newOrgForm.name.trim()}
          >
            Create Organisation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Contact Dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Contact</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Creating contact for: <strong>{selectedOrganisation?.name}</strong>
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={newContactForm.first_name}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={newContactForm.last_name}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newContactForm.email}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mobile"
                  value={newContactForm.mobile}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, mobile: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateContact}
            variant="contained"
            disabled={!newContactForm.first_name.trim() || !newContactForm.last_name.trim()}
          >
            Create Contact
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganisationContactSelector;
